import { euro, dollar } from "../lib/format";
import { useContent } from "../content/ContentContext";

// Analytical palette (editable in Admin → Editor → Site text → Chart palette).
export const DEFAULT_PALETTE = {
  navy: "#17324D",   // primary metrics, main series, chart headings
  blue: "#3274A1",   // secondary series, comparisons
  orange: "#BF652B", // highlights, contrasting series, bottlenecks
  teal: "#287D78",   // positive outcomes, improvements
  red: "#922F35",    // exceptions, overdue, losses (sparingly)
  slate: "#738296",  // baselines, previous periods, supporting data
  lavender: "#DED5EC", // occasional supporting accent
};
export const GRID = "#E7E4EC";
export const AXIS = { fontSize: 11, fill: "#6D6B78" };
export const TOOLTIP_STYLE = { borderRadius: 12, border: `1px solid ${GRID}`, fontSize: 12 };
export const CURSOR = { fill: "#F4F2F7" };

export function usePalette() {
  const { content } = useContent();
  return { ...DEFAULT_PALETTE, ...(content.site.chartPalette || {}) };
}

export function ChartTitle({ children, color }) {
  return (
    <p className="mb-3 font-mono text-[10px] font-semibold uppercase tracking-[0.16em]" style={{ color }}>
      {children}
    </p>
  );
}

export function LegendDots({ items }) {
  return (
    <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1.5 text-[10px] font-semibold uppercase tracking-[0.12em] text-muted" data-testid="chart-legend">
      {items.map((it) => (
        <span key={it.label} className="flex items-center gap-1.5">
          <span
            aria-hidden="true"
            className={`inline-block h-2.5 w-2.5 ${it.shape === "line" ? "h-0.5 w-4" : "rounded-sm"}`}
            style={{ background: it.dashed ? "transparent" : it.color, border: it.dashed ? `2px dashed ${it.color}` : "none" }}
          />
          {it.label}
        </span>
      ))}
    </div>
  );
}

export function StatusPill({ color, icon: Icon, children, testid }) {
  return (
    <span
      data-testid={testid}
      className="inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-semibold"
      style={{ color, borderColor: color, background: `${color}14` }}
    >
      {Icon && <Icon aria-hidden="true" className="h-3 w-3" strokeWidth={2.5} />}
      {children}
    </span>
  );
}

export function DashFrame({ title, note, filters, onReset, children, testid }) {
  return (
    <section
      data-testid={testid}
      className="overflow-hidden rounded-2xl border border-line bg-white"
    >
      <div className="flex flex-wrap items-center justify-between gap-4 border-b border-line bg-lavender/60 px-5 py-4 sm:px-7">
        <div>
          <h3 className="font-serif text-lg font-semibold text-ink">{title}</h3>
          <p className="mt-0.5 font-mono text-[10px] uppercase tracking-[0.16em] text-muted">
            {note || "Illustrative reconstruction using representative sample data."}
          </p>
        </div>
        {onReset && (
          <button
            data-testid="dash-reset-button"
            onClick={onReset}
            className="rounded-full border border-line bg-white px-4 py-2 text-xs font-semibold text-ink transition-colors hover:border-plum hover:text-plum"
          >
            Reset filters
          </button>
        )}
      </div>
      {filters && (
        <div className="flex flex-wrap items-end gap-3 border-b border-line px-5 py-4 sm:px-7" data-testid="dash-filters">
          {filters}
        </div>
      )}
      <div className="px-5 py-6 sm:px-7">{children}</div>
    </section>
  );
}

export function KpiCard({ label, value, sub, accent }) {
  return (
    <div data-testid="dash-kpi" className="rounded-xl border border-line bg-cream/70 p-4" style={{ borderLeft: `4px solid ${accent || DEFAULT_PALETTE.navy}` }}>
      <p className="font-mono text-[10px] uppercase tracking-[0.16em] text-muted">{label}</p>
      <p className="mt-1 font-serif text-xl font-semibold sm:text-2xl" style={{ color: accent || "#28272B" }}>{value}</p>
      {sub && <p className="mt-0.5 text-xs text-muted">{sub}</p>}
    </div>
  );
}

export function FilterSelect({ label, value, onChange, options, testid }) {
  return (
    <label className="block">
      <span className="mb-1 block font-mono text-[10px] uppercase tracking-[0.14em] text-muted">
        {label}
      </span>
      <select
        data-testid={testid}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="rounded-xl border border-line bg-white px-3 py-2 text-sm text-ink focus:border-accent"
      >
        {options.map((o) => (
          <option key={o.value ?? o} value={o.value ?? o}>
            {o.label ?? o}
          </option>
        ))}
      </select>
    </label>
  );
}

export function DataTable({ columns, rows, testid }) {
  return (
    <div className="max-h-80 overflow-auto rounded-xl border border-line" data-testid={testid || "dash-table"}>
      <table className="w-full min-w-[640px] border-collapse text-left text-xs">
        <thead className="sticky top-0 bg-cream backdrop-blur">
          <tr className="font-mono text-[10px] font-semibold uppercase tracking-[0.12em]" style={{ color: DEFAULT_PALETTE.navy }}>
            {columns.map((c) => (
              <th key={c.key} className={`px-3 py-2.5 font-medium ${c.right ? "text-right" : ""}`}>
                {c.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i} className="border-t border-line/70">
              {columns.map((c) => (
                <td key={c.key} className={`px-3 py-2 text-ink ${c.right ? "text-right" : ""}`}>
                  {c.render ? c.render(r) : r[c.key]}
                </td>
              ))}
            </tr>
          ))}
          {rows.length === 0 && (
            <tr>
              <td className="px-3 py-6 text-center text-muted" colSpan={columns.length}>
                No rows match the current filters.
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

export const tooltipEuro = (v) => euro(v);
export const tooltipDollar = (v) => dollar(v);
export const tooltipPct = (v) => `${Number(v).toFixed(1)}%`;

export function ChartCaption({ children }) {
  return <p className="mt-2 font-mono text-[10px] uppercase tracking-[0.14em] text-muted">{children}</p>;
}
